import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { MapPin, Calendar, ExternalLink, Plus, Copy, FileUp, Download, Edit2, Archive, Trash2 } from "lucide-react";
import StockMapView from "@/components/StockMapView";
import EditProjectModal from "@/components/EditProjectModal";
import ImageUploadField from "@/components/ImageUploadField";
import InlinePresentationMedia from "@/components/InlinePresentationMedia";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  UNIT_STATUSES, UNIT_TYPES, ROOM_TYPES, ASPECTS, VIEWS,
  FLOOR_POSITIONS, CURRENCIES,
} from "@/lib/constants";

const TABS = [
  { v: "overview", l: "Genel Bakış" },
  { v: "blocks", l: "Bloklar & Katlar" },
  { v: "stock-map", l: "Kat Stok Haritası" },
  { v: "units", l: "Bağımsız Bölümler" },
  { v: "interests", l: "Müşteri İlgileri" },
  { v: "sales", l: "Rezervasyon/Kapora/Satış" },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");
  const [stockMap, setStockMap] = useState([]);
  const [currentBlockId, setCurrentBlockId] = useState(null);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockForm, setBlockForm] = useState({
    name: "", block_type: "Konut Bloğu", total_floors: 8,
    auto_create_floors: true, include_basement: 0, include_ground: true, include_roof: false,
    units_per_floor: 0, floor_plans: [],
  });
  const [editBlock, setEditBlock] = useState(null);
  const [editBlockForm, setEditBlockForm] = useState({
    name: "", block_type: "Konut Bloğu", total_floors: 0, status: "Aktif",
    description: "", include_basement: 0, include_ground: false, include_roof: false,
    auto_create_floors: false, units_per_floor: 0, floor_plans: [],
  });
  const [addFloorBlockId, setAddFloorBlockId] = useState("");
  const [editFloor, setEditFloor] = useState(null);
  const [floorForm, setFloorForm] = useState({
    name: "", floor_order: 1, floor_type: "Normal Kat", units_count: 0,
    default_unit_type: "Daire", sale_enabled: true, unit_no_prefix: "", unit_no_start: 101, plan_url: "", note: "",
  });
  const [units, setUnits] = useState([]);
  const [interests, setInterests] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [sales, setSales] = useState([]);
  const [floorsByBlock, setFloorsByBlock] = useState({});
  const [users, setUsers] = useState([]);
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [unitForm, setUnitForm] = useState({
    project_id: id, block_id: "", floor_id: "", unit_no: "",
    unit_type: "Daire", room_type: "2+1", layout_type: "",
    net_area: 0, gross_area: 0, aspect: "Güney", view: "Şehir",
    floor_position: "Ara Kat", price: 0, currency: "TRY",
    status: "Satışta", sales_agent_id: "", plan_url: "",
    image_urls_text: "", video_url: "", seating_plan_url: "",
    note: "",
  });
  const [showEditProject, setShowEditProject] = useState(false);
  // Unit status filter (driven by clickable StatBox cards)
  const [unitStatusFilter, setUnitStatusFilter] = useState("");
  // Template download fallback visibility
  const [showTemplateFallback, setShowTemplateFallback] = useState(false);

  const canEdit = user?.role !== "agent";
  const canDelete = user?.role === "admin";
  const NON_SELLABLE_UNIT_TYPES = ["Depo", "Otopark"];
  const isSaleEnabledByDefault = (unitType) => !NON_SELLABLE_UNIT_TYPES.includes(unitType);

  const defaultUnitNoStart = (order) => {
    const n = Number(order) || 0;
    if (n > 0) return n * 100 + 1;
    if (n === 0) return 1;
    return Math.abs(n) * 100 + 1;
  };

  const plannedFloorsFor = (form) => {
    const rows = [];
    const basementCount = Number(form.include_basement) || 0;
    for (let i = basementCount; i >= 1; i -= 1) {
      rows.push({ floor_order: -i, name: `-${i}. Kat`, floor_type: "Bodrum" });
    }
    if (form.include_ground) rows.push({ floor_order: 0, name: "Zemin Kat", floor_type: "Zemin" });
    for (let i = 1; i <= (Number(form.total_floors) || 0); i += 1) {
      rows.push({ floor_order: i, name: `${i}. Kat`, floor_type: "Normal Kat" });
    }
    if (form.include_roof) {
      const roofOrder = (Number(form.total_floors) || 0) + 1;
      rows.push({ floor_order: roofOrder, name: "Çatı Katı", floor_type: "Çatı" });
    }
    return rows;
  };

  const floorPlanFor = (form, order) => {
    const existing = (form.floor_plans || []).find((p) => Number(p.floor_order) === Number(order));
    const defaultType = existing?.default_unit_type || "Daire";
    const rawUnitsCount = existing?.units_count;
    const rawStart = existing?.unit_no_start;
    return {
      floor_order: Number(order),
      units_count: rawUnitsCount === "" ? "" : Number(rawUnitsCount ?? form.units_per_floor ?? 0) || 0,
      default_unit_type: defaultType,
      sale_enabled: existing?.sale_enabled ?? isSaleEnabledByDefault(defaultType),
      unit_no_prefix: existing?.unit_no_prefix || "",
      unit_no_start: rawStart === "" ? "" : Number(rawStart ?? defaultUnitNoStart(order)) || 1,
    };
  };

  const floorPlansPayloadFor = (form) => plannedFloorsFor(form).map((floor) => ({
    ...floor,
    ...floorPlanFor(form, floor.floor_order),
  }));

  const sampleUnitNumbersFor = (plan) => {
    const count = Math.min(Number(plan.units_count) || 0, 5);
    if (count <= 0) return "";
    const start = Number(plan.unit_no_start) || 1;
    const prefix = plan.unit_no_prefix || "";
    const samples = Array.from({ length: count }, (_, i) => `${prefix}${start + i}`);
    const more = Number(plan.units_count) > count ? "..." : "";
    return [...samples, more].filter(Boolean).join(", ");
  };

  const updateFloorPlan = (form, setForm, order, patch) => {
    const next = floorPlanFor(form, order);
    const floor_plans = [
      ...(form.floor_plans || []).filter((p) => Number(p.floor_order) !== Number(order)),
      { ...next, ...patch, floor_order: Number(order) },
    ].sort((a, b) => Number(a.floor_order) - Number(b.floor_order));
    setForm({ ...form, floor_plans });
  };

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/stats`),
      ]);
      setProject(p);
      setStats(s);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);

  const loadStockMap = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}/stock-map`);
    setStockMap(data);
    setCurrentBlockId((current) => data.some((block) => block.id === current) ? current : (data[0]?.id || null));
  }, [id]);

  const loadUnits = useCallback(async () => {
    const { data } = await api.get("/units", { params: { project_id: id } });
    setUnits(data);
  }, [id]);

  useEffect(() => { load(); loadStockMap(); }, [load, loadStockMap]);

  useEffect(() => {
    if (tab === "units" || tab === "interests" || tab === "sales") loadUnits();
    if (tab === "sales") {
      api.get("/reservations").then(({ data }) => setReservations(data.filter((r) => r.project_id === id)));
      api.get("/deposits").then(({ data }) => setDeposits(data.filter((d) => d.project_id === id)));
      api.get("/sales").then(({ data }) => setSales(data.filter((s) => s.project_id === id)));
    }
    if (tab === "units") {
      api.get("/company/users").then(({ data }) => setUsers(data));
    }
  }, [tab, id, loadUnits]);

  // load floors for selected block in unit form
  useEffect(() => {
    if (unitForm.block_id && !floorsByBlock[unitForm.block_id]) {
      api.get(`/blocks/${unitForm.block_id}/floors`).then(({ data }) => {
        setFloorsByBlock((m) => ({ ...m, [unitForm.block_id]: data }));
      });
    }
  }, [unitForm.block_id, floorsByBlock]);

  const createBlock = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${id}/blocks`, {
        ...blockForm,
        floor_plans: floorPlansPayloadFor(blockForm),
      });
      toast.success("Blok ve katlar oluşturuldu");
      setShowBlockForm(false);
      setBlockForm({ name: "", block_type: "Konut Bloğu", total_floors: 8,
                     auto_create_floors: true, include_basement: 0, include_ground: true, include_roof: false,
                     units_per_floor: 0, floor_plans: [] });
      loadStockMap(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEditBlock = (block) => {
    const floors = block.floors || [];
    const normalFloors = floors.filter((f) => Number(f.floor_order) > 0 && f.floor_type !== "Çatı");
    const basements = floors.filter((f) => Number(f.floor_order) < 0).length;
    setEditBlock(block);
    setEditBlockForm({
      name: block.name || "",
      block_type: block.block_type || "Konut Bloğu",
      total_floors: block.total_floors || normalFloors.length || 0,
      status: block.status || "Aktif",
      description: block.description || "",
      include_basement: basements,
      include_ground: floors.some((f) => Number(f.floor_order) === 0),
      include_roof: floors.some((f) => f.floor_type === "Çatı" || Number(f.floor_order) === (block.total_floors || normalFloors.length || 0) + 1),
      auto_create_floors: false,
      units_per_floor: 0,
      floor_plans: floors.map((f) => ({
        floor_order: Number(f.floor_order) || 0,
        units_count: Number(f.units_count) || 0,
        default_unit_type: f.default_unit_type || "Daire",
        sale_enabled: f.sale_enabled ?? isSaleEnabledByDefault(f.default_unit_type || "Daire"),
        unit_no_prefix: f.unit_no_prefix || "",
        unit_no_start: Number(f.unit_no_start) || defaultUnitNoStart(f.floor_order),
      })),
    });
  };

  const createMissingFloors = async (block, form) => {
    const currentFloors = block.floors || [];
    const orders = new Set(currentFloors.map((f) => Number(f.floor_order)));
    const requests = [];
    const floorPlans = floorPlansPayloadFor(form);
    const planByOrder = new Map(floorPlans.map((p) => [Number(p.floor_order), p]));
    const planFor = (order) => planByOrder.get(Number(order)) || floorPlanFor(form, order);
    for (let i = Number(form.include_basement) || 0; i >= 1; i -= 1) {
      if (!orders.has(-i)) {
        const plan = planFor(-i);
        requests.push(() => api.post(`/blocks/${block.id}/floors`, {
          name: `-${i}. Kat`, floor_order: -i, floor_type: "Bodrum",
          units_count: Number(plan.units_count) || 0,
          default_unit_type: plan.default_unit_type || "Daire",
          sale_enabled: plan.sale_enabled ?? isSaleEnabledByDefault(plan.default_unit_type || "Daire"),
          unit_no_prefix: plan.unit_no_prefix || "",
          unit_no_start: Number(plan.unit_no_start) || defaultUnitNoStart(-i),
        }));
      }
    }
    if (form.include_ground && !orders.has(0)) {
      const plan = planFor(0);
      requests.push(() => api.post(`/blocks/${block.id}/floors`, {
        name: "Zemin Kat", floor_order: 0, floor_type: "Zemin",
        units_count: Number(plan.units_count) || 0,
        default_unit_type: plan.default_unit_type || "Daire",
        sale_enabled: plan.sale_enabled ?? isSaleEnabledByDefault(plan.default_unit_type || "Daire"),
        unit_no_prefix: plan.unit_no_prefix || "",
        unit_no_start: Number(plan.unit_no_start) || defaultUnitNoStart(0),
      }));
    }
    for (let i = 1; i <= (Number(form.total_floors) || 0); i += 1) {
      if (!orders.has(i)) {
        const plan = planFor(i);
        requests.push(() => api.post(`/blocks/${block.id}/floors`, {
          name: `${i}. Kat`, floor_order: i, floor_type: "Normal Kat",
          units_count: Number(plan.units_count) || 0,
          default_unit_type: plan.default_unit_type || "Daire",
          sale_enabled: plan.sale_enabled ?? isSaleEnabledByDefault(plan.default_unit_type || "Daire"),
          unit_no_prefix: plan.unit_no_prefix || "",
          unit_no_start: Number(plan.unit_no_start) || defaultUnitNoStart(i),
        }));
      }
    }
    const roofOrder = (Number(form.total_floors) || 0) + 1;
    if (form.include_roof && !orders.has(roofOrder)) {
      const plan = planFor(roofOrder);
      requests.push(() => api.post(`/blocks/${block.id}/floors`, {
        name: "Çatı Katı", floor_order: roofOrder, floor_type: "Çatı",
        units_count: Number(plan.units_count) || 0,
        default_unit_type: plan.default_unit_type || "Daire",
        sale_enabled: plan.sale_enabled ?? isSaleEnabledByDefault(plan.default_unit_type || "Daire"),
        unit_no_prefix: plan.unit_no_prefix || "",
        unit_no_start: Number(plan.unit_no_start) || defaultUnitNoStart(roofOrder),
      }));
    }
    for (const request of requests) await request();
    return requests.length;
  };

  const submitEditBlock = async (e) => {
    e.preventDefault();
    if (!editBlock) return;
    try {
      await api.put(`/blocks/${editBlock.id}`, {
        name: editBlockForm.name,
        block_type: editBlockForm.block_type,
        total_floors: Number(editBlockForm.total_floors) || 0,
        status: editBlockForm.status,
        description: editBlockForm.description,
      });
      for (const floor of editBlock.floors || []) {
        await api.put(`/floors/${floor.id}`, {
          units_count: Number(floorPlanFor(editBlockForm, floor.floor_order).units_count) || 0,
          default_unit_type: floorPlanFor(editBlockForm, floor.floor_order).default_unit_type || "Daire",
          sale_enabled: floorPlanFor(editBlockForm, floor.floor_order).sale_enabled ?? true,
          unit_no_prefix: floorPlanFor(editBlockForm, floor.floor_order).unit_no_prefix || "",
          unit_no_start: Number(floorPlanFor(editBlockForm, floor.floor_order).unit_no_start) || defaultUnitNoStart(floor.floor_order),
        });
      }
      const added = editBlockForm.auto_create_floors ? await createMissingFloors(editBlock, editBlockForm) : 0;
      toast.success(added ? `Blok güncellendi, ${added} eksik kat eklendi` : "Blok güncellendi");
      setEditBlock(null);
      loadStockMap(); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openAddFloor = (block) => {
    const floors = block.floors || [];
    const maxOrder = floors.reduce((m, f) => Math.max(m, Number(f.floor_order) || 0), 0);
    setAddFloorBlockId(block.id);
    setFloorForm({
      name: `${maxOrder + 1}. Kat`, floor_order: maxOrder + 1,
      floor_type: "Normal Kat", units_count: 0,
      default_unit_type: "Daire", sale_enabled: true, unit_no_prefix: "", unit_no_start: defaultUnitNoStart(maxOrder + 1),
      plan_url: "", note: "",
    });
  };

  const submitFloor = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/blocks/${addFloorBlockId}/floors`, {
        ...floorForm,
        floor_order: Number(floorForm.floor_order) || 0,
        units_count: Number(floorForm.units_count) || 0,
        default_unit_type: floorForm.default_unit_type || "Daire",
        sale_enabled: floorForm.sale_enabled ?? isSaleEnabledByDefault(floorForm.default_unit_type || "Daire"),
        unit_no_start: Number(floorForm.unit_no_start) || defaultUnitNoStart(floorForm.floor_order),
      });
      toast.success("Kat eklendi");
      setAddFloorBlockId("");
      loadStockMap(); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyPresentationLink = () => {
    const link = `${window.location.origin}/p/projects/${id}`;
    navigator.clipboard.writeText(link);
    toast.success("Sunum linki kopyalandı");
  };

  const copyInventoryLink = () => {
    const link = `${window.location.origin}/p/projects/${id}?inventory=1`;
    navigator.clipboard.writeText(link);
    toast.success("Envanter ve stok linki kopyalandı");
  };

  const archiveProject = async () => {
    if (!project) return;
    if (!window.confirm(`"${project.name}" projesini PASİFE almak istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/projects/${id}?force=true`);
      toast.success("Proje pasife alındı");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteProject = async () => {
    if (!project) return;
    if (!window.confirm(`"${project.name}" projesini SİLMEK istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Proje silindi");
      nav("/projects");
    } catch (err) {
      const det = err?.response?.data?.detail;
      if (det && typeof det === "object" && det.detail) {
        if (window.confirm(`${det.detail}\n\nBağlı: ${det.blocks} blok, ${det.units} bölüm, ${det.sales} satış.\n\nProjeyi PASİFE almak ister misiniz?`)) {
          archiveProject();
        }
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  const onProjectSaved = (updated) => {
    setProject(updated);
  };

  const downloadTemplate = async (format) => {
    setShowTemplateFallback(true);
    try {
      const res = await api.get(`/units-template?format=${format}`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `stok_sablonu.${format === "xlsx" ? "xlsx" : "csv"}`;
      a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("İndirme başladı. Dosya inmezse aşağıdaki doğrudan indirme bağlantısını kullanın.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteBlock = async (block) => {
    if (!window.confirm(`"${block.name}" bloğunu silmek veya pasife almak istiyor musunuz?\n\nBağlı bölüm varsa geçmiş korunarak pasife alınır. Aktif rezervasyon, kapora veya satış varsa işlem engellenir.`)) return;
    try {
      const { data } = await api.delete(`/blocks/${block.id}`);
      toast.success(data.archived ? `"${block.name}" pasife alındı` : `"${block.name}" silindi`);
      loadStockMap(); load();
    } catch (err) {
      const det = err?.response?.data?.detail;
      if (det && typeof det === "object" && det.detail) {
        toast.error(`${det.detail}\nBağlı: ${det.units || 0} bölüm, ${det.interests || 0} ilgi, ${det.reservations || 0} rezervasyon, ${det.deposits || 0} kapora, ${det.sales || 0} satış.`);
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  const openEditFloor = (floor) => {
    setEditFloor(floor);
    setFloorForm({
      name: floor.name || "",
      floor_order: Number(floor.floor_order) || 0,
      floor_type: floor.floor_type || "Normal Kat",
      units_count: Number(floor.units_count) || 0,
      default_unit_type: floor.default_unit_type || "Daire",
      sale_enabled: floor.sale_enabled ?? isSaleEnabledByDefault(floor.default_unit_type || "Daire"),
      unit_no_prefix: floor.unit_no_prefix || "",
      unit_no_start: Number(floor.unit_no_start) || defaultUnitNoStart(floor.floor_order),
      plan_url: floor.plan_url || "",
      note: floor.note || "",
    });
  };

  const submitEditFloor = async (e) => {
    e.preventDefault();
    if (!editFloor) return;
    try {
      await api.put(`/floors/${editFloor.id}`, {
        ...floorForm,
        floor_order: Number(floorForm.floor_order) || 0,
        units_count: Number(floorForm.units_count) || 0,
        default_unit_type: floorForm.default_unit_type || "Daire",
        sale_enabled: floorForm.sale_enabled ?? isSaleEnabledByDefault(floorForm.default_unit_type || "Daire"),
        unit_no_start: Number(floorForm.unit_no_start) || defaultUnitNoStart(floorForm.floor_order),
      });
      toast.success("Kat güncellendi");
      setEditFloor(null);
      loadStockMap(); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteFloor = async (floor) => {
    if (!window.confirm(`"${floor.name}" katını silmek veya pasife almak istiyor musunuz?\n\nBağlı bölüm varsa geçmiş korunarak pasife alınır. Aktif rezervasyon, kapora veya satış varsa işlem engellenir.`)) return;
    try {
      const { data } = await api.delete(`/floors/${floor.id}`);
      toast.success(data.archived ? `"${floor.name}" pasife alındı` : `"${floor.name}" silindi`);
      loadStockMap(); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitUnit = async (e) => {
    e.preventDefault();
    if (!unitForm.block_id || !unitForm.floor_id) {
      toast.error("Blok ve kat seçimi zorunludur");
      return;
    }
    if (plannedUnitsOnSelectedFloor > 0 && existingUnitsOnSelectedFloor.length >= plannedUnitsOnSelectedFloor) {
      toast.error(`Bu katta planlanan ${plannedUnitsOnSelectedFloor} bağımsız bölüm dolu`);
      return;
    }
    // Duplicate check
    const dup = units.find((u) => u.project_id === id && u.block_id === unitForm.block_id
                                    && u.floor_id === unitForm.floor_id
                                    && (u.unit_no || "").trim().toLowerCase() === unitForm.unit_no.trim().toLowerCase());
    if (dup) {
      toast.error("Bu bağımsız bölüm zaten kayıtlı.");
      return;
    }
    try {
      const payload = { ...unitForm,
        net_area: Number(unitForm.net_area) || 0,
        gross_area: Number(unitForm.gross_area) || 0,
        price: Number(unitForm.price) || 0,
        sales_agent_id: unitForm.sales_agent_id || null,
        image_urls: (unitForm.image_urls_text || "").split(/\r?\n/).map((x) => x.trim()).filter(Boolean),
      };
      delete payload.image_urls_text;
      await api.post("/units", payload);
      toast.success("Bağımsız bölüm eklendi");
      setShowUnitForm(false);
      setUnitForm({ project_id: id, block_id: "", floor_id: "", unit_no: "",
                    unit_type: "Daire", room_type: "2+1", layout_type: "",
                    net_area: 0, gross_area: 0, aspect: "Güney", view: "Şehir",
                    floor_position: "Ara Kat", price: 0, currency: "TRY",
                    status: "Satışta", sales_agent_id: "", plan_url: "",
                    image_urls_text: "", video_url: "", seating_plan_url: "",
                    note: "" });
      load(); loadUnits(); loadStockMap();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const selectedBlockForUnit = stockMap.find((b) => b.id === unitForm.block_id);
  const selectedFloorForUnit = (floorsByBlock[unitForm.block_id] || []).find((f) => f.id === unitForm.floor_id);
  const existingUnitsOnSelectedFloor = units.filter((u) => u.block_id === unitForm.block_id && u.floor_id === unitForm.floor_id);
  const plannedUnitsOnSelectedFloor = Number(selectedFloorForUnit?.units_count) || 0;
  const makeUnitNo = (index) => {
    const prefix = selectedFloorForUnit?.unit_no_prefix || "";
    const start = Number(selectedFloorForUnit?.unit_no_start) || defaultUnitNoStart(selectedFloorForUnit?.floor_order);
    return `${prefix}${start + index - 1}`;
  };
  const unitNoOptions = plannedUnitsOnSelectedFloor > 0
    ? Array.from({ length: plannedUnitsOnSelectedFloor }, (_, i) => makeUnitNo(i + 1))
    : [];
  const availableUnitNoOptions = unitNoOptions.filter((no) => !existingUnitsOnSelectedFloor.some((u) => (u.unit_no || "").toLowerCase() === no.toLowerCase()));
  const unitPlanStatus = selectedFloorForUnit?.sale_enabled === false ? "Pasif" : "Satışta";

  useEffect(() => {
    if (!selectedFloorForUnit) return;
    const patch = {};
    if (selectedFloorForUnit.default_unit_type && unitForm.unit_type !== selectedFloorForUnit.default_unit_type) {
      patch.unit_type = selectedFloorForUnit.default_unit_type;
    }
    if (selectedFloorForUnit.sale_enabled === false && unitForm.status !== "Pasif") {
      patch.status = "Pasif";
    }
    if (Object.keys(patch).length) {
      setUnitForm((form) => ({ ...form, ...patch }));
    }
  }, [selectedFloorForUnit, unitForm.unit_type, unitForm.status]);

  if (!project) return <div className="text-slate-500">Yükleniyor…</div>;

  return (
    <div className="min-w-0 space-y-6" data-testid="project-detail-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-slate-500">{project.project_type} • {project.sale_type}</div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-display" data-testid="project-title">{project.name}</h1>
          <div className="mt-2 flex items-center gap-3 text-sm text-slate-600 flex-wrap">
            <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {project.location || `${project.city} ${project.district}` || "—"}</span>
            {project.delivery_date && <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" /> {project.delivery_date}</span>}
            <span className="status-chip status-Satışta"><span className="status-dot" /> {project.status}</span>
          </div>
        </div>
        <div className="grid w-full min-w-0 grid-cols-2 gap-2 md:flex md:w-auto md:flex-wrap md:items-center">
          {canEdit && (
            <button onClick={() => setShowEditProject(true)}
                    className="min-w-0 bg-white border border-slate-200 hover:border-slate-300 px-3 py-2 text-sm rounded-lg inline-flex items-center justify-center gap-2 text-center leading-tight"
                    data-testid="edit-project-btn">
              <Edit2 className="w-4 h-4 shrink-0" /> Projeyi Düzenle
            </button>
          )}
          {canEdit && (
            <button onClick={() => archiveProject()}
                    className="min-w-0 bg-white border border-slate-200 hover:border-slate-300 px-3 py-2 text-sm rounded-lg inline-flex items-center justify-center gap-2 text-center leading-tight"
                    data-testid="archive-project-btn">
              <Archive className="w-4 h-4 shrink-0" /> Pasife Al
            </button>
          )}
          {canDelete && (
            <button onClick={() => deleteProject()}
                    className="min-w-0 bg-white border border-red-200 hover:bg-red-50 text-red-700 px-3 py-2 text-sm rounded-lg inline-flex items-center justify-center gap-2 text-center leading-tight"
                    data-testid="delete-project-btn">
              <Trash2 className="w-4 h-4 shrink-0" /> Sil
            </button>
          )}
          <button onClick={copyPresentationLink}
                  className="min-w-0 bg-white border border-slate-200 hover:border-slate-300 px-3 py-2 text-sm rounded-lg inline-flex items-center justify-center gap-2 text-center leading-tight"
                  data-testid="copy-presentation-link">
            <Copy className="w-4 h-4 shrink-0" /> Sunum Linki
          </button>
          <button onClick={copyInventoryLink} disabled={project.public_inventory_enabled === false}
                  className="min-w-0 bg-white border border-slate-200 hover:border-slate-300 px-3 py-2 text-sm rounded-lg inline-flex items-center justify-center gap-2 text-center leading-tight"
                  data-testid="copy-inventory-link">
            <Copy className="w-4 h-4 shrink-0" /> Envanter Linki
          </button>
          <Link to={`/p/projects/${id}`} target="_blank"
                className="min-w-0 bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 text-sm rounded-lg inline-flex items-center justify-center gap-2 text-center leading-tight"
                data-testid="open-presentation">
            <ExternalLink className="w-4 h-4 shrink-0" /> Sunumda Aç
          </Link>
        </div>
      </div>

      {/* Cover */}
      {project.cover_image_url && (
        <div className="aspect-[21/8] w-full overflow-hidden rounded-xl bg-slate-100">
          <img src={project.cover_image_url} alt={project.name} className="w-full h-full object-cover" />
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatBox label="Toplam" value={stats.total} testid="stat-total"
                   onClick={() => { setUnitStatusFilter(""); setTab("units"); }} />
          <StatBox label="Satışta" value={stats["Satışta"]} color="#10B981" testid="stat-satista"
                   onClick={() => { setUnitStatusFilter("Satışta"); setTab("units"); }} />
          <StatBox label="Rezerve" value={stats["Rezerve"]} color="#3B82F6" testid="stat-rezerve"
                   onClick={() => { setUnitStatusFilter("Rezerve"); setTab("units"); }} />
          <StatBox label="Kapora" value={stats["Kapora Alındı"]} color="#F59E0B" testid="stat-kapora"
                   onClick={() => { setUnitStatusFilter("Kapora Alındı"); setTab("units"); }} />
          <StatBox label="Satıldı" value={stats["Satıldı"]} color="#334155" testid="stat-satildi"
                   onClick={() => { setUnitStatusFilter("Satıldı"); setTab("units"); }} />
          <StatBox label="Güncellemede" value={stats["Güncellemede"]} color="#8B5CF6" testid="stat-güncellemede"
                   onClick={() => { setUnitStatusFilter("Güncellemede"); setTab("units"); }} />
        </div>
      )}
      {stats && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-slate-700">Satış Oranı</div>
            <div className="text-sm font-bold text-slate-900" data-testid="sale-ratio">%{stats.sale_ratio}</div>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 transition-all" style={{ width: `${Math.min(100, stats.sale_ratio || 0)}%` }} />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 overflow-x-auto">
        <div className="flex gap-2">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)}
                    data-testid={`tab-${t.v}`}
                    className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${tab === t.v ? "border-amber-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Kısa Açıklama</div>
              <p className="mt-1 text-slate-700">{project.short_description || "—"}</p>
            </div>
            {project.long_description && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Detay</div>
                <p className="mt-1 text-slate-700 whitespace-pre-line">{project.long_description}</p>
              </div>
            )}
            {(project.address || project.map_link) && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Adres</div>
                <p className="mt-1 text-slate-700">{project.address || "—"}</p>
                {project.map_link && (
                  <a href={project.map_link} target="_blank" rel="noreferrer" className="text-amber-600 text-sm underline">Haritada gör</a>
                )}
              </div>
            )}
          </div>

          {/* Medya önizleme */}
          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="media-preview">
            <div className="text-sm font-semibold text-slate-900 mb-3">Medya &amp; Görseller</div>
            {(project.gallery_images || []).length === 0 && !project.brochure_url && !project.floor_plan_url && !project.site_plan_url ? (
              <div className="text-sm text-slate-400">Görsel henüz eklenmedi. Sağ üstten "Projeyi Düzenle" ile ekleyebilirsiniz.</div>
            ) : (
              <>
                {(project.gallery_images || []).length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Galeri ({project.gallery_images.length})</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {project.gallery_images.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noreferrer" className="block aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden">
                          <img src={url} alt="" className="w-full h-full object-cover hover:scale-105 transition" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2 text-sm">
                  {project.brochure_url && <a href={project.brochure_url} target="_blank" rel="noreferrer" className="px-3 py-1.5 border border-slate-200 rounded-lg hover:border-amber-400">Broşür PDF</a>}
                </div>
                <InlinePresentationMedia title="Kat Planı" urls={[project.floor_plan_url]} />
                <InlinePresentationMedia title="Vaziyet Planı" urls={[project.site_plan_url]} />
              </>
            )}
          </div>
        </div>
      )}

      {tab === "blocks" && (
        <div className="space-y-4">
          {canEdit && (
            <button onClick={() => setShowBlockForm(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg px-4 py-2 inline-flex items-center gap-2 text-sm"
                    data-testid="add-block-btn">
              <Plus className="w-4 h-4" /> Yeni Blok / Etap
            </button>
          )}
          {showBlockForm && (
            <form onSubmit={createBlock} className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="block-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Blok Adı</div>
                  <input required value={blockForm.name} onChange={(e) => setBlockForm({ ...blockForm, name: e.target.value })}
                         className="input" data-testid="block-name-input" />
                </label>
                <label className="text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Tip</div>
                  <select value={blockForm.block_type} onChange={(e) => setBlockForm({ ...blockForm, block_type: e.target.value })}
                          className="input">
                    {["Konut Bloğu", "Ticari Blok", "Karma Blok", "Villa Etabı"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Normal Kat Sayısı</div>
                  <input type="number" min={0} value={blockForm.total_floors}
                         onChange={(e) => setBlockForm({ ...blockForm, total_floors: Number(e.target.value) })}
                         className="input" data-testid="block-floors-input" />
                </label>
                <label className="text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Bodrum Kat Sayısı</div>
                  <input type="number" min={0} value={blockForm.include_basement}
                         onChange={(e) => setBlockForm({ ...blockForm, include_basement: Number(e.target.value) })}
                         className="input" />
                </label>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={blockForm.include_ground}
                         onChange={(e) => setBlockForm({ ...blockForm, include_ground: e.target.checked })} />
                  Zemin Kat
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={blockForm.include_roof}
                         onChange={(e) => setBlockForm({ ...blockForm, include_roof: e.target.checked })} />
                  Çatı Katı
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={blockForm.auto_create_floors}
                         onChange={(e) => setBlockForm({ ...blockForm, auto_create_floors: e.target.checked })} />
                  Katları otomatik oluştur
                </label>
              </div>
              {blockForm.auto_create_floors && (
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Katlara Göre Bölüm Planı</div>
                  </div>
                  <div className="overflow-x-auto">
                  <div className="min-w-[1120px] divide-y divide-slate-100">
                    <div className="hidden md:grid grid-cols-[1fr_170px_130px_100px_130px_130px] gap-3 px-3 py-2 bg-white text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      <div>Kat</div>
                      <div>Bölüm Tipi</div>
                      <div>Bölüm Sayısı</div>
                      <div>Satış</div>
                      <div>No Ön Eki</div>
                      <div>İlk No</div>
                    </div>
                    {floorPlansPayloadFor(blockForm).map((floor) => {
                      const plan = floorPlanFor(blockForm, floor.floor_order);
                      return (
                        <div key={floor.floor_order} className="grid grid-cols-1 md:grid-cols-[1fr_170px_130px_100px_130px_130px] gap-3 p-2 md:p-3 items-end">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{floor.name}</div>
                            <div className="text-xs text-slate-500">{floor.floor_type}</div>
                            {sampleUnitNumbersFor(plan) && (
                              <div className="text-[11px] text-slate-400 mt-1">Örnek: {sampleUnitNumbersFor(plan)}</div>
                            )}
                          </div>
                          <label className="text-sm">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 md:sr-only">Bölüm Tipi</div>
                            <select value={UNIT_TYPES.includes(plan.default_unit_type) ? plan.default_unit_type : "Diğer"}
                                    onChange={(e) => updateFloorPlan(blockForm, setBlockForm, floor.floor_order, {
                                      default_unit_type: e.target.value,
                                      sale_enabled: isSaleEnabledByDefault(e.target.value),
                                    })}
                                    className="input">
                              {UNIT_TYPES.map((t) => <option key={t}>{t}</option>)}
                            </select>
                            {(plan.default_unit_type === "Diğer" || !UNIT_TYPES.includes(plan.default_unit_type)) && (
                              <input className="input mt-2" aria-label={`${floor.name} özel bölüm tipi`} placeholder="Bölüm tipini yazın"
                                value={plan.default_unit_type === "Diğer" ? "" : plan.default_unit_type}
                                onChange={(e) => updateFloorPlan(blockForm, setBlockForm, floor.floor_order, { default_unit_type: e.target.value || "Diğer" })} />
                            )}
                          </label>
                          <label className="text-sm">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 md:sr-only">Bölüm Sayısı</div>
                            <input type="number" min={0} value={plan.units_count}
                                   onChange={(e) => updateFloorPlan(blockForm, setBlockForm, floor.floor_order, { units_count: e.target.value })}
                                   className="input" />
                          </label>
                          <label className="text-sm flex md:justify-center items-center gap-2 md:pb-3">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 md:sr-only">Satışa Açık</div>
                            <input type="checkbox" checked={Boolean(plan.sale_enabled)}
                                   onChange={(e) => updateFloorPlan(blockForm, setBlockForm, floor.floor_order, { sale_enabled: e.target.checked })}
                                   className="h-4 w-4" />
                          </label>
                          <label className="text-sm">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 md:sr-only">No Ön Eki</div>
                            <input value={plan.unit_no_prefix}
                                   onChange={(e) => updateFloorPlan(blockForm, setBlockForm, floor.floor_order, { unit_no_prefix: e.target.value })}
                                   className="input" placeholder="A, B, Lobi" />
                          </label>
                          <label className="text-sm">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 md:sr-only">İlk No</div>
                            <input type="number" min={1} value={plan.unit_no_start}
                                   onChange={(e) => updateFloorPlan(blockForm, setBlockForm, floor.floor_order, { unit_no_start: e.target.value })}
                                   className="input" />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowBlockForm(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg" data-testid="submit-block">Oluştur</button>
              </div>
            </form>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="blocks-grid">
            {stockMap.map((b) => (
              <div key={b.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`block-card-${b.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-lg font-semibold text-slate-900">{b.name}</div>
                    <div className="text-xs text-slate-500">{b.block_type}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="status-chip status-Satışta"><span className="status-dot" /> {b.status}</span>
                    {canEdit && (
                      <button type="button"
                              onClick={() => openEditBlock(b)}
                              title="Bloğu Düzenle"
                              className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md p-1 transition">
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    {canDelete && (
                      <button type="button"
                              onClick={() => deleteBlock(b)}
                              title="Bloğu Sil"
                              data-testid={`delete-block-${b.id}`}
                              className="text-red-500 hover:text-white hover:bg-red-500 border border-red-200 hover:border-red-500 rounded-md p-1 transition">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  {b.floors?.length || 0} kat • {b.floors?.reduce((a, f) => a + (f.units?.length || 0), 0)} bölüm
                </div>
                {canEdit && (
                  <button type="button" onClick={() => openAddFloor(b)}
                          className="mt-3 text-xs font-medium text-amber-700 hover:text-amber-900">
                    + Yeni Kat Ekle
                  </button>
                )}
                <ul className="mt-3 text-xs text-slate-600 max-h-32 overflow-y-auto">
                  {(b.floors || []).map((f) => (
                    <li key={f.id} className="flex justify-between py-1 border-b border-slate-100 last:border-b-0">
                      <span className={f.status === "Pasif" ? "line-through text-slate-400" : ""}>{f.name}</span>
                      <span className="inline-flex items-center gap-1">
                        <span className="text-slate-400">{f.units?.length || 0}</span>
                        {canEdit && (
                          <button type="button" onClick={() => openEditFloor(f)} title="Katı Düzenle"
                                  className="text-slate-400 hover:text-slate-900">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button type="button" onClick={() => deleteFloor(f)} title="Katı Sil"
                                  className="text-red-400 hover:text-red-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "stock-map" && (
        <StockMapView blocks={stockMap} currentBlockId={currentBlockId} setCurrentBlockId={setCurrentBlockId}
                      onUnitClick={(u) => nav(`/units/${u.id}`)} />
      )}

      {tab === "units" && (
        <div className="space-y-4">
          {canEdit && (
            <div className="flex flex-wrap gap-2" data-testid="units-tab-actions">
              <button onClick={() => setShowUnitForm(true)}
                      className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2"
                      data-testid="new-unit-btn">
                <Plus className="w-4 h-4" /> Planlı Bölümü Detaylandır
              </button>
              <button onClick={() => downloadTemplate("xlsx")}
                      className="bg-white border border-slate-200 hover:border-slate-300 rounded-lg px-3 py-2 text-sm inline-flex items-center gap-2"
                      data-testid="download-xlsx-btn">
                <Download className="w-4 h-4" /> Excel Şablonu İndir
              </button>
              <button onClick={() => downloadTemplate("csv")}
                      className="bg-white border border-slate-200 hover:border-slate-300 rounded-lg px-3 py-2 text-sm inline-flex items-center gap-2"
                      data-testid="download-csv-btn">
                <Download className="w-4 h-4" /> CSV Şablonu İndir
              </button>
              <Link to="/import"
                    className="bg-white border border-slate-200 hover:border-slate-300 rounded-lg px-3 py-2 text-sm inline-flex items-center gap-2"
                    data-testid="upload-units-link">
                <FileUp className="w-4 h-4" /> Excel/CSV Stok Yükle
              </Link>
            </div>
          )}
          {showTemplateFallback && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm" data-testid="template-fallback">
              <div className="font-semibold text-amber-900 mb-1">Otomatik indirme başlamadıysa…</div>
              <div className="text-amber-800 text-xs mb-2">Bazı tarayıcı/önizleme ortamlarında otomatik indirme engellenebilir. Aşağıdaki linklere tıklayarak şablonu doğrudan açın/indirin:</div>
              <div className="flex flex-wrap gap-2">
                <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/units-template?format=xlsx`}
                   target="_blank" rel="noreferrer"
                   download="stok_sablonu.xlsx"
                   data-testid="template-fallback-xlsx"
                   className="px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-amber-900 hover:bg-amber-100 inline-flex items-center gap-2">
                  <Download className="w-4 h-4" /> Excel Şablonunu Doğrudan Aç / İndir
                </a>
                <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/units-template?format=csv`}
                   target="_blank" rel="noreferrer"
                   download="stok_sablonu.csv"
                   data-testid="template-fallback-csv"
                   className="px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-amber-900 hover:bg-amber-100 inline-flex items-center gap-2">
                  <Download className="w-4 h-4" /> CSV Şablonunu Doğrudan Aç / İndir
                </a>
                <button type="button" onClick={() => setShowTemplateFallback(false)}
                        className="text-xs text-amber-700 hover:text-amber-900 underline">Gizle</button>
              </div>
              <div className="text-[11px] text-amber-700 mt-2">Not: Doğrudan link tarayıcıda oturum gerektirir. Otomatik indirme zaten başladıysa bu bölümü görmezden gelebilirsiniz.</div>
            </div>
          )}
          <UnitTable
            units={unitStatusFilter ? units.filter((u) => u.status === unitStatusFilter) : units}
            onClick={(u) => nav(`/units/${u.id}`)}
            filterLabel={unitStatusFilter}
            onClearFilter={() => setUnitStatusFilter("")}
          />
        </div>
      )}

      <Dialog open={showUnitForm} onOpenChange={setShowUnitForm}>
        <DialogContent className="unit-detail-dialog w-[calc(100%-2rem)] max-w-[960px] max-h-[calc(100dvh-2rem)] flex flex-col gap-0 overflow-hidden p-0" data-testid="new-unit-modal">
          <DialogHeader className="shrink-0 border-b border-slate-100 px-5 py-3 text-left">
            <DialogTitle className="text-base pr-6">Planlı Bölümü Detaylandır</DialogTitle>
            <DialogDescription className="text-xs">{project.name}</DialogDescription>
          </DialogHeader>
            <form onSubmit={submitUnit} className="flex min-h-0 flex-col overflow-hidden" data-testid="new-unit-form">
              <div className="min-h-0 overflow-y-auto overscroll-contain px-5 py-3 space-y-3">
              <div className="unit-form-section">
                <div className="text-xs font-semibold text-slate-700 mb-2">Plan Seçimi</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-2">
                <UF label="Blok" required>
                  <select required className="input" value={unitForm.block_id}
                          onChange={(e) => setUnitForm({ ...unitForm, block_id: e.target.value, floor_id: "", unit_no: "" })}
                          data-testid="new-unit-block">
                    <option value="">Blok seçin</option>
                    {stockMap.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </UF>
                <UF label="Kat" required>
                  <select required className="input" value={unitForm.floor_id}
                          onChange={(e) => setUnitForm({ ...unitForm, floor_id: e.target.value, unit_no: "" })}
                          disabled={!unitForm.block_id}
                          data-testid="new-unit-floor">
                    <option value="">Kat seçin</option>
                    {(floorsByBlock[unitForm.block_id] || []).map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </UF>
                <UF label="Planlı Bölüm" required>
                  {plannedUnitsOnSelectedFloor > 0 ? (
                    <select required className="input" value={unitForm.unit_no}
                            onChange={(e) => setUnitForm({ ...unitForm, unit_no: e.target.value })}
                            data-testid="new-unit-no">
                      <option value="">Detaylandırılacak bölümü seçin</option>
                      {availableUnitNoOptions.map((no) => <option key={no} value={no}>{no}</option>)}
                    </select>
                  ) : (
                    <input required className="input" value={unitForm.unit_no}
                           onChange={(e) => setUnitForm({ ...unitForm, unit_no: e.target.value })}
                           data-testid="new-unit-no" />
                  )}
                  {selectedFloorForUnit && (
                    <div className="mt-1 text-[11px] text-slate-500">
                      {plannedUnitsOnSelectedFloor > 0
                        ? `${existingUnitsOnSelectedFloor.length}/${plannedUnitsOnSelectedFloor} bölüm kayıtlı`
                        : "Bu katta planlanan bölüm sayısı tanımlı değil"}
                    </div>
                  )}
                </UF>
                </div>
              </div>

              {selectedFloorForUnit && (
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Plan Özeti</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-2 text-sm">
                    <div>
                      <div className="text-xs text-slate-500">Bölüm</div>
                      <div className="font-semibold text-slate-900">{unitForm.unit_no || "Seçilmedi"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Bölüm Tipi</div>
                      <div className="font-semibold text-slate-900">{unitForm.unit_type}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Satış Durumu</div>
                      <div className="font-semibold text-slate-900">{unitPlanStatus}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Kat Kapasitesi</div>
                      <div className="font-semibold text-slate-900">{plannedUnitsOnSelectedFloor || "Tanımsız"}</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="unit-form-section">
                <div className="text-xs font-semibold text-slate-700 mb-2">Bölüm Detayları</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-x-3 gap-y-2">
                <UF label="Bölüm Tipi">
                  <select className="input" value={UNIT_TYPES.includes(unitForm.unit_type) ? unitForm.unit_type : "Diğer"}
                          onChange={(e) => setUnitForm({ ...unitForm, unit_type: e.target.value })}
                          disabled={Boolean(selectedFloorForUnit?.default_unit_type)}>
                    {UNIT_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  {selectedFloorForUnit?.default_unit_type && (
                    <div className="mt-1 text-[11px] text-slate-500">Kat planından otomatik geldi.</div>
                  )}
                  {!selectedFloorForUnit?.default_unit_type && (unitForm.unit_type === "Diğer" || !UNIT_TYPES.includes(unitForm.unit_type)) ? (
                    <input className="input mt-2" placeholder="Bölüm tipini yazın"
                           value={UNIT_TYPES.includes(unitForm.unit_type) ? "" : unitForm.unit_type}
                           onChange={(e) => setUnitForm({ ...unitForm, unit_type: e.target.value || "Diğer" })} />
                  ) : null}
                </UF>
                <UF label="Oda Tipi">
                  <select className="input" value={ROOM_TYPES.includes(unitForm.room_type) ? unitForm.room_type : "Diğer"} onChange={(e) => setUnitForm({ ...unitForm, room_type: e.target.value })}>
                    {ROOM_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  {unitForm.room_type === "Diğer" || !ROOM_TYPES.includes(unitForm.room_type) ? (
                    <input className="input mt-2" placeholder="Oda tipini yazın"
                           value={ROOM_TYPES.includes(unitForm.room_type) ? "" : unitForm.room_type}
                           onChange={(e) => setUnitForm({ ...unitForm, room_type: e.target.value || "Diğer" })} />
                  ) : null}
                </UF>
                <UF label="Plan / Daire Tipi">
                  <input className="input" placeholder="A Tipi, B Tipi, Loft Tipi"
                         value={unitForm.layout_type}
                         onChange={(e) => setUnitForm({ ...unitForm, layout_type: e.target.value })} />
                </UF>
                <UF label="Net m²"><input type="number" className="input" value={unitForm.net_area} onChange={(e) => setUnitForm({ ...unitForm, net_area: e.target.value })} /></UF>
                <UF label="Brüt m²"><input type="number" className="input" value={unitForm.gross_area} onChange={(e) => setUnitForm({ ...unitForm, gross_area: e.target.value })} /></UF>
                <UF label="Cephe">
                  <select className="input" value={unitForm.aspect} onChange={(e) => setUnitForm({ ...unitForm, aspect: e.target.value })}>
                    {ASPECTS.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </UF>
                <UF label="Manzara">
                  <select className="input" value={unitForm.view} onChange={(e) => setUnitForm({ ...unitForm, view: e.target.value })}>
                    {VIEWS.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </UF>
                <UF label="Kat Konumu">
                  <select className="input" value={unitForm.floor_position} onChange={(e) => setUnitForm({ ...unitForm, floor_position: e.target.value })}>
                    {FLOOR_POSITIONS.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </UF>

                </div>
              </div>

              <div className="unit-form-section">
                <div className="text-xs font-semibold text-slate-700 mb-2">Satış Bilgileri</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-x-3 gap-y-2">
                <UF label="Fiyat"><input type="number" className="input" value={unitForm.price} onChange={(e) => setUnitForm({ ...unitForm, price: e.target.value })} data-testid="new-unit-price" /></UF>
                <UF label="Para Birimi">
                  <select className="input" value={unitForm.currency} onChange={(e) => setUnitForm({ ...unitForm, currency: e.target.value })}>
                    {CURRENCIES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </UF>
                <UF label="Sorumlu Personel">
                  <select className="input" value={unitForm.sales_agent_id} onChange={(e) => setUnitForm({ ...unitForm, sales_agent_id: e.target.value })}>
                    <option value="">— Atama yok —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </UF>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Durum</div>
                  <div className="input flex items-center text-slate-700 bg-slate-50">{unitPlanStatus}</div>
                  {selectedFloorForUnit?.sale_enabled === false && (
                    <div className="mt-1 text-[11px] text-slate-500">Bu kat satışa kapalı olduğu için bölüm Pasif kaydedilir.</div>
                  )}
                </div>
                </div>
              </div>
              <details className="rounded-lg border border-slate-200 px-3 py-2">
                <summary className="cursor-pointer text-xs font-medium text-slate-600">Görseller, bağlantılar ve not <span className="text-slate-400">(isteğe bağlı)</span></summary>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-x-3 gap-y-2 pt-3">
                <div className="md:col-span-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Daire / Bölüm Planı</div>
                  <ImageUploadField value={unitForm.plan_url} onChange={(url) => setUnitForm((form) => ({ ...form, plan_url: url }))} compact />
                </div>
                <UF label="Video URL"><input className="input" value={unitForm.video_url} onChange={(e) => setUnitForm({ ...unitForm, video_url: e.target.value })} /></UF>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Kat / Konum Planı</div>
                  <ImageUploadField value={unitForm.seating_plan_url} onChange={(url) => setUnitForm((form) => ({ ...form, seating_plan_url: url }))} compact />
                </div>
                <div className="md:col-span-2">
                  <UF label="Örnek Daire Görselleri">
                    <textarea rows={3} className="input resize-none" placeholder="Her satıra bir görsel URL'si"
                              value={unitForm.image_urls_text}
                              onChange={(e) => setUnitForm({ ...unitForm, image_urls_text: e.target.value })} />
                  </UF>
                </div>
                <div className="md:col-span-4">
                  <UF label="Not"><textarea rows={2} className="input resize-none" value={unitForm.note} onChange={(e) => setUnitForm({ ...unitForm, note: e.target.value })} /></UF>
                </div>
                </div>
              </details>
              </div>
              <div className="flex shrink-0 justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
                <button type="button" onClick={() => setShowUnitForm(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg" data-testid="submit-new-unit">Detayları Kaydet</button>
              </div>
            </form>
        </DialogContent>
      </Dialog>

      {tab === "interests" && <InterestsByProject projectId={id} />}

      {tab === "sales" && (
        <div className="space-y-6">
          <SalesSection title="Rezervasyonlar" items={reservations} type="reservation" />
          <SalesSection title="Kaporalar" items={deposits} type="deposit" />
          <SalesSection title="Satışlar" items={sales} type="sale" />
        </div>
      )}

      {showEditProject && (
        <EditProjectModal project={project} onClose={() => setShowEditProject(false)} onSaved={onProjectSaved} />
      )}

      {editBlock && (
        <div className="fixed inset-0 z-50 !mt-0 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setEditBlock(null)}>
          <div className="my-auto w-full max-w-7xl rounded-xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-lg font-semibold text-slate-900 mb-4 font-display">Blok / Etap Düzenle</div>
            <form onSubmit={submitEditBlock} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Blok Adı</div>
                  <input required value={editBlockForm.name} onChange={(e) => setEditBlockForm({ ...editBlockForm, name: e.target.value })} className="input" />
                </label>
                <label className="text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Tip</div>
                  <select value={editBlockForm.block_type} onChange={(e) => setEditBlockForm({ ...editBlockForm, block_type: e.target.value })} className="input">
                    {["Konut Bloğu", "Ticari Blok", "Karma Blok", "Villa Etabı"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Normal Kat Sayısı</div>
                  <input type="number" min={0} value={editBlockForm.total_floors}
                         onChange={(e) => setEditBlockForm({ ...editBlockForm, total_floors: Number(e.target.value) })}
                         className="input" />
                </label>
                <label className="text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Bodrum Kat Sayısı</div>
                  <input type="number" min={0} value={editBlockForm.include_basement}
                         onChange={(e) => setEditBlockForm({ ...editBlockForm, include_basement: Number(e.target.value) })}
                         className="input" />
                </label>
                <label className="text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Durum</div>
                  <select value={editBlockForm.status} onChange={(e) => setEditBlockForm({ ...editBlockForm, status: e.target.value })} className="input">
                    {["Aktif", "Pasif"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Açıklama</div>
                  <input value={editBlockForm.description} onChange={(e) => setEditBlockForm({ ...editBlockForm, description: e.target.value })} className="input" />
                </label>
              </div>
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Mevcut Kat Bölüm Planı</div>
                </div>
                <div className="overflow-auto max-h-80">
                <div className="min-w-[1120px] divide-y divide-slate-100">
                  <div className="hidden md:grid grid-cols-[1fr_170px_130px_100px_130px_130px] gap-3 px-3 py-2 bg-white text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <div>Kat</div>
                    <div>Bölüm Tipi</div>
                    <div>Bölüm Sayısı</div>
                    <div>Satış</div>
                    <div>No Ön Eki</div>
                    <div>İlk No</div>
                  </div>
                  {(editBlock.floors || [])
                    .slice()
                    .sort((a, b) => Number(a.floor_order) - Number(b.floor_order))
                    .map((floor) => {
                      const plan = floorPlanFor(editBlockForm, floor.floor_order);
                      return (
                        <div key={floor.id} className="grid grid-cols-1 md:grid-cols-[1fr_170px_130px_100px_130px_130px] gap-3 p-2 md:p-3 items-end">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{floor.name}</div>
                            <div className="text-xs text-slate-500">{floor.floor_type}</div>
                            {sampleUnitNumbersFor(plan) && (
                              <div className="text-[11px] text-slate-400 mt-1">Örnek: {sampleUnitNumbersFor(plan)}</div>
                            )}
                          </div>
                          <label className="text-sm">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 md:sr-only">Bölüm Tipi</div>
                            <select value={UNIT_TYPES.includes(plan.default_unit_type) ? plan.default_unit_type : "Diğer"}
                                    onChange={(e) => updateFloorPlan(editBlockForm, setEditBlockForm, floor.floor_order, {
                                      default_unit_type: e.target.value,
                                      sale_enabled: isSaleEnabledByDefault(e.target.value),
                                    })}
                                    className="input">
                              {UNIT_TYPES.map((t) => <option key={t}>{t}</option>)}
                            </select>
                            {(plan.default_unit_type === "Diğer" || !UNIT_TYPES.includes(plan.default_unit_type)) && (
                              <input className="input mt-2" aria-label={`${floor.name} özel bölüm tipi`} placeholder="Bölüm tipini yazın"
                                value={plan.default_unit_type === "Diğer" ? "" : plan.default_unit_type}
                                onChange={(e) => updateFloorPlan(editBlockForm, setEditBlockForm, floor.floor_order, { default_unit_type: e.target.value || "Diğer" })} />
                            )}
                          </label>
                          <label className="text-sm">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 md:sr-only">Bölüm Sayısı</div>
                            <input type="number" min={0} value={plan.units_count}
                                   onChange={(e) => updateFloorPlan(editBlockForm, setEditBlockForm, floor.floor_order, { units_count: e.target.value })}
                                   className="input" />
                          </label>
                          <label className="text-sm flex md:justify-center items-center gap-2 md:pb-3">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 md:sr-only">Satışa Açık</div>
                            <input type="checkbox" checked={Boolean(plan.sale_enabled)}
                                   onChange={(e) => updateFloorPlan(editBlockForm, setEditBlockForm, floor.floor_order, { sale_enabled: e.target.checked })}
                                   className="h-4 w-4" />
                          </label>
                          <label className="text-sm">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 md:sr-only">No Ön Eki</div>
                            <input value={plan.unit_no_prefix}
                                   onChange={(e) => updateFloorPlan(editBlockForm, setEditBlockForm, floor.floor_order, { unit_no_prefix: e.target.value })}
                                   className="input" placeholder="A, B, Lobi" />
                          </label>
                          <label className="text-sm">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 md:sr-only">İlk No</div>
                            <input type="number" min={1} value={plan.unit_no_start}
                                   onChange={(e) => updateFloorPlan(editBlockForm, setEditBlockForm, floor.floor_order, { unit_no_start: e.target.value })}
                                   className="input" />
                          </label>
                        </div>
                      );
                    })}
                </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={editBlockForm.include_ground}
                         onChange={(e) => setEditBlockForm({ ...editBlockForm, include_ground: e.target.checked })} />
                  Zemin Kat
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={editBlockForm.include_roof}
                         onChange={(e) => setEditBlockForm({ ...editBlockForm, include_roof: e.target.checked })} />
                  Çatı Katı
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={editBlockForm.auto_create_floors}
                         onChange={(e) => setEditBlockForm({ ...editBlockForm, auto_create_floors: e.target.checked })} />
                  Eksik katları oluştur
                </label>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Kat sayısını düşürmek mevcut katları otomatik silmez. İçinde bölüm olan katlar ayrıca güvenli silme ile yönetilir.
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditBlock(null)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editFloor && (
        <div className="fixed inset-0 z-50 !mt-0 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setEditFloor(null)}>
          <div className="my-auto w-full max-w-lg rounded-xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-lg font-semibold text-slate-900 mb-4 font-display">Kat Düzenle</div>
            <form onSubmit={submitEditFloor} className="space-y-3">
              <FloorFields
                floorForm={floorForm}
                setFloorForm={setFloorForm}
                unitTypes={UNIT_TYPES}
                isSaleEnabledByDefault={isSaleEnabledByDefault}
              />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditFloor(null)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {addFloorBlockId && (
        <div className="fixed inset-0 z-50 !mt-0 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setAddFloorBlockId("")}>
          <div className="my-auto w-full max-w-lg rounded-xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-lg font-semibold text-slate-900 mb-4 font-display">Yeni Kat Ekle</div>
            <form onSubmit={submitFloor} className="space-y-3">
              <FloorFields
                floorForm={floorForm}
                setFloorForm={setFloorForm}
                unitTypes={UNIT_TYPES}
                isSaleEnabledByDefault={isSaleEnabledByDefault}
              />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setAddFloorBlockId("")} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg">Katı Ekle</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FloorFields({ floorForm, setFloorForm, unitTypes, isSaleEnabledByDefault }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <label className="text-sm">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Kat Adı</div>
        <input required value={floorForm.name} onChange={(e) => setFloorForm({ ...floorForm, name: e.target.value })} className="input" />
      </label>
      <label className="text-sm">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Kat Sırası</div>
        <input type="number" value={floorForm.floor_order} onChange={(e) => setFloorForm({ ...floorForm, floor_order: e.target.value })} className="input" />
      </label>
      <label className="text-sm col-span-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Kat Tipi</div>
        <select value={floorForm.floor_type} onChange={(e) => setFloorForm({ ...floorForm, floor_type: e.target.value })} className="input">
          {["Bodrum", "Zemin", "Normal Kat", "Çatı", "Ticari Kat", "Bahçe"].map((t) => <option key={t}>{t}</option>)}
        </select>
      </label>
      <label className="text-sm col-span-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Planlanan Bölüm Sayısı</div>
        <input type="number" min={0} value={floorForm.units_count}
               onChange={(e) => setFloorForm({ ...floorForm, units_count: e.target.value })}
               className="input" />
      </label>
      <label className="text-sm col-span-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Bölüm Tipi</div>
        <select value={unitTypes.includes(floorForm.default_unit_type) ? floorForm.default_unit_type : "Diğer"}
                onChange={(e) => setFloorForm({
                  ...floorForm,
                  default_unit_type: e.target.value,
                  sale_enabled: isSaleEnabledByDefault(e.target.value),
                })}
                className="input">
          {unitTypes.map((t) => <option key={t}>{t}</option>)}
        </select>
        {(floorForm.default_unit_type === "Diğer" || !unitTypes.includes(floorForm.default_unit_type)) && (
          <input className="input mt-2" aria-label="Özel bölüm tipi" placeholder="Bölüm tipini yazın"
            value={floorForm.default_unit_type === "Diğer" ? "" : floorForm.default_unit_type}
            onChange={(e) => setFloorForm({ ...floorForm, default_unit_type: e.target.value || "Diğer" })} />
        )}
      </label>
      <label className="text-sm col-span-2 inline-flex items-center gap-2">
        <input type="checkbox" checked={Boolean(floorForm.sale_enabled)}
               onChange={(e) => setFloorForm({ ...floorForm, sale_enabled: e.target.checked })}
               className="h-4 w-4" />
        Satışa açık alan
      </label>
      <label className="text-sm">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">No Ön Eki</div>
        <input value={floorForm.unit_no_prefix}
               onChange={(e) => setFloorForm({ ...floorForm, unit_no_prefix: e.target.value })}
               className="input" placeholder="A, B, Lobi" />
      </label>
      <label className="text-sm">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">İlk Bölüm No</div>
        <input type="number" min={1} value={floorForm.unit_no_start}
               onChange={(e) => setFloorForm({ ...floorForm, unit_no_start: e.target.value })}
               className="input" />
      </label>
      <div className="text-sm col-span-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Kat Planı</div>
        <ImageUploadField value={floorForm.plan_url} onChange={(url) => setFloorForm((form) => ({ ...form, plan_url: url }))} compact />
      </div>
      <label className="text-sm col-span-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Not</div>
        <textarea rows={2} value={floorForm.note} onChange={(e) => setFloorForm({ ...floorForm, note: e.target.value })} className="input resize-none" />
      </label>
    </div>
  );
}

function StatBox({ label, value, color, testid, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`bg-white border border-slate-200 rounded-xl p-4 text-left w-full ${onClick ? "hover:border-amber-400 hover:shadow-sm transition cursor-pointer" : ""}`}
    >
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold font-display flex items-baseline gap-2" style={{ color: color || "#0F172A" }}>
        {value ?? 0}
      </div>
    </Tag>
  );
}

function UF({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}{required && <span className="text-red-500"> *</span>}</div>
      {children}
    </label>
  );
}

function UnitTable({ units, onClick, filterLabel, onClearFilter }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden overflow-x-auto" data-testid="project-unit-table">
      {filterLabel && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs flex items-center justify-between">
          <span className="text-amber-800 font-medium">Filtre: {filterLabel} ({units.length} sonuç)</span>
          <button type="button" onClick={onClearFilter} data-testid="clear-unit-filter"
                  className="text-xs px-2 py-0.5 bg-white border border-amber-300 rounded hover:bg-amber-100">Tümünü göster</button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-4 py-3">Bölüm No</th>
            <th className="text-left px-4 py-3">Proje / Blok / Kat</th>
            <th className="text-left px-4 py-3">Tip</th>
            <th className="text-left px-4 py-3">Oda</th>
            <th className="text-left px-4 py-3">Plan Tipi</th>
            <th className="text-left px-4 py-3">m² (Net/Brüt)</th>
            <th className="text-left px-4 py-3">Cephe</th>
            <th className="text-left px-4 py-3">Manzara</th>
            <th className="text-left px-4 py-3">Personel</th>
            <th className="text-left px-4 py-3">Fiyat</th>
            <th className="text-left px-4 py-3">Durum</th>
          </tr>
        </thead>
        <tbody>
          {units.map((u) => (
            <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                onClick={() => onClick?.(u)} data-testid={`unit-row-${u.id}`}>
              <td className="px-4 py-3 font-semibold text-slate-900">{u.unit_no}</td>
              <td className="px-4 py-3 text-xs">
                <div className="font-medium text-slate-700">{u.project_name || "Proje bilgisi yok"}</div>
                <div className="text-slate-500">{u.block_name || "Blok bilgisi yok"} / {u.floor_name || "Kat bilgisi yok"}</div>
              </td>
              <td className="px-4 py-3">{u.unit_type}</td>
              <td className="px-4 py-3">{u.room_type}</td>
              <td className="px-4 py-3">{u.layout_type || "—"}</td>
              <td className="px-4 py-3">{u.net_area}/{u.gross_area}</td>
              <td className="px-4 py-3">{u.aspect}</td>
              <td className="px-4 py-3">{u.view}</td>
              <td className="px-4 py-3 text-xs text-slate-600">{u.sales_agent_name || "—"}</td>
              <td className="px-4 py-3 font-semibold">{Math.round(u.price).toLocaleString("tr-TR")} {u.currency}</td>
              <td className="px-4 py-3"><span className={`status-chip ${"status-" + (u.status === "Kapora Alındı" ? "Kapora" : u.status?.replace(/\s+/g, ""))}`}><span className="status-dot" />{u.status}</span></td>
            </tr>
          ))}
          {units.length === 0 && (
            <tr><td colSpan={11} className="px-4 py-12 text-center text-slate-400">Bölüm yok.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SalesSection({ title, items, type }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`sales-section-${type}`}>
      <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">{title} ({items.length})</div>
      {items.length === 0 ? (
        <div className="px-4 py-8 text-sm text-slate-400 text-center">Kayıt yok</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((i) => (
            <li key={i.id} className="px-4 py-3 text-sm flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900">{i.unit_no || "—"}</div>
                <div className="text-xs text-slate-500">
                  {i.project_name || "Proje bilgisi yok"} / {i.block_name || "Blok bilgisi yok"} / {i.floor_name || "Kat bilgisi yok"}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  Müşteri: <span className="font-medium">{i.customer_name || "—"}</span>
                  {i.sales_agent_name ? <> • Personel: {i.sales_agent_name}</> : null}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-slate-500">{(i.created_at || "").slice(0, 10)}</div>
                {i.amount && <div className="font-semibold">{Number(i.amount).toLocaleString("tr-TR")} {i.currency}</div>}
                {i.sale_price && <div className="font-semibold">{Number(i.sale_price).toLocaleString("tr-TR")} {i.currency}</div>}
                {i.status && <div className="text-xs text-slate-600">{i.status}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InterestsByProject({ projectId }) {
  const [units, setUnits] = useState([]);
  useEffect(() => {
    api.get("/units", { params: { project_id: projectId, has_interest: true } }).then(({ data }) => setUnits(data));
  }, [projectId]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">İlgilenilen Bölümler ({units.length})</div>
      <UnitTable units={units} onClick={(u) => window.location.href = `/units/${u.id}`} />
    </div>
  );
}
